import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  Alert,
  TextField,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import axios from 'axios';

interface School {
  id: string;
  name: string;
  code: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  website?: string;
  isActive: boolean;
  branchName?: string;
  billingName?: string;
  accountStartYear?: number;
  sessionStartMonth?: number;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: {
    name: string;
    code: string;
  };
  isActive: boolean;
}

/**
 * School Detail Page (SuperAdmin)
 * Shows school details in a read-only form and admin list
 */
const SchoolDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [school, setSchool] = useState<School | null>(null);
  const [instituteData, setInstituteData] = useState<any>({});
  const [admins, setAdmins] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedBranchProfile, setSelectedBranchProfile] = useState<any | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      const [schoolRes, profileRes, adminsRes, branchesRes] = await Promise.all([
        axios.get(`/schools/${id}`),
        axios.get(`/schools/${id}/institute-profile`),
        axios.get('/system-settings/users', { params: { schoolId: id } }),
        axios.get(`/schools/${id}/branches`),
      ]);

      setSchool(schoolRes.data);
      if (profileRes.data) {
        setInstituteData(profileRes.data);
      }
      setAdmins(adminsRes.data);
      setBranches(branchesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Container maxWidth="lg">
          <Typography>Loading...</Typography>
        </Container>
      </MainLayout>
    );
  }

  if (!school) {
    return (
      <MainLayout>
        <Container maxWidth="lg">
          <Alert severity="error">School not found</Alert>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/superadmin/schools')}
            sx={{ mb: 2 }}
          >
            Back to Schools
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4">{school.name}</Typography>
              <Typography variant="body2" color="textSecondary">
                {school.code}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/superadmin/schools/${school.id}/edit`)}
              >
                Edit
              </Button>
            </Box>
          </Box>
        </Box>

        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              School Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="School Name"
                  value={school.name}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="School Code"
                  value={school.code}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Status"
                  value={school.isActive ? 'Active' : 'Inactive'}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={school.email || ''}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Phone No"
                  value={school.phone || ''}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={school.address || ''}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={school.city || ''}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={school.state || ''}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Country"
                  value={school.country || ''}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="PIN"
                  value={school.postalCode}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Billing Name"
                  value={school.billingName || ''}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Start Year"
                  value={school.accountStartYear || ''}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Session Start Month"
                  value={school.sessionStartMonth || ''}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>

              
            </Grid>
          </Box>
        </Paper>

        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Branches
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Branch Name</TableCell>
                    <TableCell>Branch Code</TableCell>
                    <TableCell>Billing Name</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {branches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No branches found
                      </TableCell>
                    </TableRow>
                  ) : (
                    branches.map((b: any) => (
                      <TableRow key={b.id}>
                        <TableCell>{b.branchName || '-'}</TableCell>
                        <TableCell>{b.branchCode || '-'}</TableCell>
                        <TableCell>{b.billingName || '-'}</TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            onClick={async () => {
                              try {
                                const res = await axios.get(`/schools/${id}/branches/${b.id}/institute-profile`);
                                setSelectedBranchProfile(res.data);
                              } catch (err) {
                                setSelectedBranchProfile(null);
                              }
                            }}
                          >
                            View Profile
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {selectedBranchProfile && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Branch Institute Profile
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Reg./Aff. No" value={selectedBranchProfile.regAffNo || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Principal Name" value={selectedBranchProfile.principalName || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Address 1" value={selectedBranchProfile.address1 || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Address 2" value={selectedBranchProfile.address2 || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="District" value={selectedBranchProfile.district || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="PIN" value={selectedBranchProfile.pin || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Website" value={selectedBranchProfile.websiteName || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Email Address" value={selectedBranchProfile.emailAddress || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth label="Phone No" value={selectedBranchProfile.phoneNo || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth label="Mobile" value={selectedBranchProfile.mobile || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Scholar No Prefix" value={selectedBranchProfile.scholarNoPrefix || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Scholar No Suffix" value={selectedBranchProfile.scholarNoSuffix || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Employee No Prefix" value={selectedBranchProfile.empNoPrefix || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Fax No" value={selectedBranchProfile.faxNo || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Accredited By" value={selectedBranchProfile.accreditedBy || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Pages in TC Book" value={selectedBranchProfile.tcBookPages || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Background Image URL" value={selectedBranchProfile.backgroundImage || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Input Text Format" value={selectedBranchProfile.inputTextFormat || ''} InputProps={{ readOnly: true }} variant="filled" />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Paper>

        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              School Admins
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {admins.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No admins found
                      </TableCell>
                    </TableRow>
                  ) : (
                    admins.map((admin) => (
                      <TableRow key={admin.id}>
                        <TableCell>{`${admin.firstName} ${admin.lastName}`}</TableCell>
                        <TableCell>{admin.email}</TableCell>
                        <TableCell>{admin.role.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={admin.isActive ? 'Active' : 'Inactive'}
                            color={admin.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>

        <Paper>
          <Box sx={{ p: 3 }}>
            <Typography variant="body1" color="textSecondary">
              To manage admins and modules, please use the "Module Management" option in the main menu.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </MainLayout>
  );
};

export default SchoolDetail;
